<template>
  <div class="field-builder">
    <div class="row bottom g-1">
      <component :is="enablePresetSelect ? 'DropdownComponent' : 'div'" v-if="isNuxt && sortingConfig">
        <ButtonComponent
          :label="triggerLabel"
          icon-right="fa-pro-regular:angle-down"
          @click="enablePresetSelect ? null : (open = !open)"
        />
        <template #dropdown>
          <div>
            <ButtonComponent
              :label="buildMode ? 'Legg til felter manuelt' : $t('sortingTitle')"
              icon="fa-pro-regular:arrow-right"
              @click="open = !open"
            />

            <span class="field-builder__category">{{ `Velg et oppsett fra andre ${presetTitle.toLowerCase()}:` }}</span>
            <ButtonComponent
              v-for="value in presets"
              :label="value.title"
              icon="fa-pro-light:newspaper"
              @click="preset = value.id"
            />
          </div>
        </template>
      </component>
    </div>

    <ModalComponent :show="open">
      <Webcomponent
        class="field-builder__content"
        :value="valueComp"
        @input="$ev => update($ev)"
        data-field="sorting"
        tag="kvass-sorting"
        :config="sortingConfig"
        :mode="buildMode ? 'build' : 'sort'"
        source="https://cdn.jsdelivr.net/npm/@kvass/sorting@latest/dist/sorting.js"
        :labels="
          JSON.stringify({
            addAll: 'Legg til alle',
            cancel: $t('cancel'),
            confirm: $t('confirm'),
            reset: $t('reset'),
            title: $t('sortingTitle'),
            subtitle: $t('sortingSubtitle'),
            alert: $t('sortingAlert'),
          })
        "
      />
    </ModalComponent>
  </div>
</template>

<script>
import { Filter as PlacementFilter, SortItems } from '@kvass/custom-fields/src/utils.js'
import Webcomponent from '@kvass/custom-fields/src/Fields/Webcomponent'
import { ModalComponent } from 'vue-elder-modal'
import fetch from '@/fetch'
import { DropdownComponent } from 'vue-elder-dropdown'
import getCoverThumbnail from '@/utils/getCoverThumbnail'

function getValue(path, data) {
  return path.split('.').reduce((acc, key) => {
    if (acc && key in acc) return acc[key]
  }, data)
}

function replaceNestedPlaceholders(str, data) {
  if (typeof str !== 'string') return str

  return str.replace(/{([\w.-]+)}/g, (_, path) => {
    if (!path) return str
    const value = getValue(path, data)
    return value !== undefined ? value : `{${path}}`
  })
}

function getThumbnail(str, data) {
  if (typeof str !== 'string') return str

  const [, path] = str.match(/^{([\w.-]+)}$/) || []
  if (!path) return str

  return getCoverThumbnail(getValue(path, data) || []) || 'fa-pro-regular:image'
}

export default {
  props: {
    item: Object,
    value: Array,
    customFieldSchemas: { type: Array, default: () => [] },
    placement: [Array, String],
    build: Boolean,
    selectFromPreset: Boolean,
    presetTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      open: false,
      preset: null,
      presets: [],
    }
  },
  watch: {
    preset(newValue, oldValue) {
      if (newValue) {
        const value = this.presets.find(i => i.id === newValue)

        if (value) {
          this.$emit('input', value.layout)
        }
      }
    },
  },

  computed: {
    valueComp() {
      return (this.value || []).filter(cf => {
        return this.$path('block.display.builder', cf) !== false
      })
    },
    triggerLabel() {
      if (this.enablePresetSelect || this.buildMode) return 'Rediger felter'

      return this.$t('sortingTitle')
    },
    buildMode() {
      return this.build
    },
    isNuxt() {
      if (!this.item) return
      if ((this.item.config || '').includes('custom-subpage')) return true
      const { NuxtPages, NuxtProjects } = this.$store.state.enums
      return [...NuxtPages, ...NuxtProjects].some(i =>
        [this.$path('theme.template', this.item), this.item.theme].includes(i),
      )
    },
    enablePresetSelect() {
      return this.presets.length && this.selectPreset
    },
    sortingConfig() {
      if (!this.item) return ''

      const schemas = this.customFieldSchemas.map(schema => {
        return {
          ...schema,
          placement: this.$path('block.placement', schema) || schema.placement,
          key: this.$path('block.key', schema) || schema.key,
          options: {
            ...(schema.options || {}),
            location: this.$path('block.location', schema) || this.$path('options.location', schema),
          },
        }
      })

      const filteredItems = SortItems(
        PlacementFilter(schemas, {
          placement: this.placement instanceof Array ? this.placement : [this.placement],
          keys: [],
        }),
      )
        .map(cf => {
          if (!cf.block || this.$path('block.display.builder', cf) === false) return
          return {
            ...cf.block,
            key: cf.key,
            label: cf.block.label || cf.label,
          }
        })
        .filter(Boolean)

      let mappedItems = filteredItems.map(item => {
        return Object.fromEntries(
          Object.entries(item).map(([key, value]) => {
            if (key === 'thumbnail') return [key, getThumbnail(value, this.item)]
            return [key, replaceNestedPlaceholders(value, this.item)]
          }),
        )
      })

      if (!mappedItems.length) return
      return JSON.stringify(mappedItems)
    },
  },
  methods: {
    update(value) {
      switch (value.state) {
        case 'save': {
          this.$emit('input', value.value)
          this.open = false
          break
        }
        case 'reset': {
          this.$emit('input', value.value)
          break
        }
        default: {
          this.open = false
          break
        }
      }
    },
    selectPreset() {
      return fetch(
        `/api/resource/post?field.id=slug&field.thumbnail=media.cover&field.title=title&field.layout=layout&field.source=customFieldsConfig.0.source&references=${this.item.reference.ref}&disableFilterByIds=true`,
      )
        .then(res => res.json())
        .then(data => {
          return (this.presets = data.filter(i => {
            return Boolean(i.layout && i.layout.length) && this.item.config === i.source && this.item.slug !== i.id
          }))
        })
    },
  },
  mounted() {
    if (this.selectFromPreset) {
      this.selectPreset()
    }
  },

  components: {
    Webcomponent,
    ModalComponent,
    DropdownComponent,
  },
}
</script>

<style lang="scss">
.field-builder {
  &__content {
    max-width: fit-content;
  }
  &__category {
    opacity: 0.8;
    font-weight: bold;
    border-top: 1px solid var(--border-color);

    padding: 0.75rem 1rem 0.5rem;

    display: block;
    & > * {
      padding: 0;
    }
  }
}
</style>
